import React, { useCallback, useMemo, useState } from "react";
import { FixedSizeList } from "react-window";
import SearchField from "../../common/SearchField";
import { CheckBox } from "../../common";
import SmallLoader from "../../loaders/SmallLoader";

const DEFAULT_LIST_HEIGHT = 280;
const ROW_HEIGHT = 32;
const VIRTUAL_LIST_OVERSCAN = 5;

export interface ScopeSearchableChecklistItem {
  value: string;
  label: string;
}

interface ScopeSearchableChecklistProps {
  items: ScopeSearchableChecklistItem[];
  checked: string[];
  onCheckedChange: (checked: string[]) => void;
  itemLabelSingular: string;
  itemLabelPlural: string;
  searchPlaceholder: string;
  isLoading: boolean;
  loadingMessage?: string;
  /** Optional icon shown once in the header above the list */
  itemIcon?: React.ReactNode;
  /** Max height of the virtualised list (px). Default 280. */
  listMaxHeight?: number;
}

function ScopeSearchableChecklist({
  items,
  checked,
  onCheckedChange,
  itemLabelSingular,
  itemLabelPlural,
  searchPlaceholder,
  isLoading,
  loadingMessage = "Loading...",
  itemIcon,
  listMaxHeight = DEFAULT_LIST_HEIGHT,
}: ScopeSearchableChecklistProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  const allValues = useMemo(
    () => items.map((i) => String(i.value)),
    [items]
  );

  const filteredItems = useMemo(() => {
    if (!searchQuery?.trim()) return items;
    const q = searchQuery.trim().toLowerCase();
    return items.filter((item) => item.label.toLowerCase().includes(q));
  }, [items, searchQuery]);

  const sortedItems = useMemo(() => {
    const sorted = [...filteredItems].sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { sensitivity: "base" })
    );
    return sortOrder === "desc" ? sorted.reverse() : sorted;
  }, [filteredItems, sortOrder]);

  const selectedCount = checked.length;
  const hasSearch = (searchQuery?.trim().length ?? 0) > 0;

  const checkedSet = useMemo(
    () => new Set(checked.map((id) => String(id))),
    [checked]
  );
  const isItemChecked = (value: string) => checkedSet.has(String(value));

  const handleToggle = (value: string, isChecked: boolean) => {
    const valueStr = String(value);
    if (isChecked) {
      onCheckedChange([...checked, valueStr]);
    } else {
      onCheckedChange(checked.filter((id) => String(id) !== valueStr));
    }
  };

  /** With no search: select the full list. With a search: select only items matching the current filter (merged into existing selection). */
  const handleSelectAllInList = () => {
    if (searchQuery?.trim()) {
      const filteredValues = filteredItems.map((i) => String(i.value));
      const combined = Array.from(
        new Set([...checked.map(String), ...filteredValues])
      );
      onCheckedChange(combined);
    } else {
      onCheckedChange([...allValues]);
    }
  };

  const handleClearAll = () => {
    onCheckedChange([]);
  };

  const listItemData = useMemo(
    () => ({
      filteredItems: sortedItems,
      isItemChecked,
      handleToggle,
    }),
    [sortedItems, checked]
  );

  const ListRow = useCallback(
    ({
      index,
      style,
      data,
    }: {
      index: number;
      style: React.CSSProperties;
      data: {
        filteredItems: ScopeSearchableChecklistItem[];
        isItemChecked: (value: string) => boolean;
        handleToggle: (value: string, isChecked: boolean) => void;
      };
    }) => {
      const {
        filteredItems: listItems,
        isItemChecked: check,
        handleToggle: toggle,
      } = data;
      const item = listItems[index];
      if (!item) return null;
      return (
        <div
          style={style}
          className="flex items-center gap-2 pl-4 pr-2 rounded hover:bg-gray-50 box-border"
        >
          <CheckBox
            controlId={`scope-checklist-${item.value}`}
            labelText={item.label}
            selected={check(item.value)}
            onChange={(isChecked) => toggle(item.value, isChecked)}
            disabled={false}
            checkboxClassNames="bg-white border border-gray-300 flex-shrink-0"
            labelClassNames="flex-1 text-sm truncate leading-tight"
            containerClassNames="flex-1 min-w-0 flex items-center"
            includeCheckboxContainerClass={false}
            checkboxPosition="LEFT"
          />
        </div>
      );
    },
    []
  );

  if (isLoading) {
    return (
      <div className="pt-2">
        <SmallLoader />
        <p className="text-center pt-1 text-[#959595] text-sm">
          {loadingMessage}
        </p>
      </div>
    );
  }

  return (
    <div className="pt-2">
      <div className="mb-3 min-w-[180px]">
        <SearchField
          id="scope-checklist-search"
          isLoading={false}
          onSearch={(value) => setSearchQuery(value ?? "")}
          loaderColor="rgb(156 163 175)"
          searchTerm={searchQuery}
          placeholderText={searchPlaceholder}
          includeSetWidth={false}
          outerContainerClassName="flex items-center my-0"
          debounceDelay={200}
        />
      </div>

      <div className="border border-gray-200 rounded-t-md border-b-0 bg-gray-50 px-3 py-2 flex flex-wrap items-center gap-x-3 gap-y-2 justify-between">
        <div className="flex items-center gap-2 min-w-0">
          {itemIcon && (
            <span className="flex-shrink-0 flex items-center [&>svg]:w-4 [&>svg]:h-4">
              {itemIcon}
            </span>
          )}
          <span className="text-sm font-medium text-gray-700 truncate">
            {itemLabelPlural.charAt(0).toUpperCase() + itemLabelPlural.slice(1)}
          </span>
          <span
            className="rounded-full bg-gray-200 px-2 py-0.5 text-xs font-medium text-gray-700 flex-shrink-0"
            aria-label={`${selectedCount} selected`}
          >
            {selectedCount}
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-500">
          <button
            type="button"
            onClick={handleSelectAllInList}
            className="hover:text-gray-700 hover:underline"
            disabled={allValues.length === 0}
          >
            Select all
          </button>
          <span className="text-gray-400" aria-hidden="true">
            |
          </span>
          <button
            type="button"
            onClick={handleClearAll}
            className="hover:text-gray-700 hover:underline"
          >
            Clear all
          </button>
          {filteredItems.length > 0 && (
            <>
              <span className="text-gray-400" aria-hidden="true">
                |
              </span>
              <button
                type="button"
                onClick={() =>
                  setSortOrder((o) => (o === "asc" ? "desc" : "asc"))
                }
                className="text-xs font-medium text-gray-500 hover:text-gray-700 px-2 py-1 rounded border border-gray-200 bg-white hover:bg-gray-50"
                title={sortOrder === "asc" ? "Sort Z–A" : "Sort A–Z"}
                aria-label={`Sort ${sortOrder === "asc" ? "Z–A" : "A–Z"}`}
              >
                {sortOrder === "asc" ? "A–Z" : "Z–A"}
              </button>
            </>
          )}
        </div>
      </div>

      {filteredItems.length === 0 ? (
        <div
          className="border border-gray-200 rounded-b-md mb-2 py-3 px-3"
          role="list"
        >
          <p className="text-sm text-gray-500 text-center">
            {hasSearch ? "No matching items." : "No items available."}
          </p>
        </div>
      ) : (
        <div
          className="border border-gray-200 rounded-b-md overflow-hidden mb-2"
          style={{
            height: Math.min(
              listMaxHeight,
              Math.max(ROW_HEIGHT * 2, sortedItems.length * ROW_HEIGHT)
            ),
          }}
          role="list"
        >
          <FixedSizeList
            height={Math.min(
              listMaxHeight,
              Math.max(ROW_HEIGHT * 2, sortedItems.length * ROW_HEIGHT)
            )}
            width="100%"
            itemCount={sortedItems.length}
            itemSize={ROW_HEIGHT}
            itemData={listItemData}
            overscanCount={VIRTUAL_LIST_OVERSCAN}
            className="border-0"
          >
            {ListRow}
          </FixedSizeList>
        </div>
      )}
    </div>
  );
}

export default ScopeSearchableChecklist;
